import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import type { HomeHandle, HomeState } from "./contracts";
import { safely } from "./contracts";
import { controller } from "./runtime";
import type { Controller } from "./controller";

type Location = {
  key: string;
  pathname: string;
  search: string;
  hash: string;
  state?: { brand?: unknown } | null;
};
// Match CreatorHome's actual overlay branch; a shared hash still renders Home.
export function isBrandsOverlay(location: Pick<Location, "hash" | "state">) {
  return location.hash === "#brands" && !!location.state?.brand;
}
type Navigation = { location: Location; key: string };
export function nextNavigation(
  previous: Navigation | undefined,
  location: Location
): Navigation {
  const before = previous?.location;
  const drawerOnly =
    before &&
    before.pathname === location.pathname &&
    before.search === location.search &&
    before.hash !== location.hash &&
    !isBrandsOverlay(before) &&
    !isBrandsOverlay(location);
  return {
    location,
    key: drawerOnly
      ? previous!.key
      : `${location.key}:${location.pathname}:${
          location.search
        }:${isBrandsOverlay(location)}`,
  };
}
const HomeContext = createContext<HomeHandle | undefined>(undefined);

/** Mounted outside the business error boundary, before router/auth/lazy gates. */
export function HomeNavigation({
  children,
  telemetry = controller,
}: {
  children: React.ReactNode;
  telemetry?: Controller;
}) {
  const location = useLocation();
  const previous = useRef<Navigation>();
  const [handle, setHandle] = useState<HomeHandle>();
  const { key, pathname, search, hash, state } = location;
  useLayoutEffect(() => {
    const navigation = nextNavigation(previous.current, {
      key,
      pathname,
      search,
      hash,
      state,
    });
    previous.current = navigation;
    const current = safely(
      () =>
        telemetry.enter(
          navigation.key,
          pathname === "/home" && !isBrandsOverlay(navigation.location)
        ),
      undefined
    );
    if (process.env.NODE_ENV === "development" && current)
      safely(
        () =>
          require("./localDiagnostics").localDiagnostics.navigation(
            current,
            pathname === "/home" && !isBrandsOverlay(navigation.location)
          ),
        undefined
      );
    setHandle(current);
    return () => {
      if (current) safely(() => telemetry.leave(current), undefined);
    };
  }, [telemetry, key, pathname, search, hash, state]);
  return <HomeContext.Provider value={handle}>{children}</HomeContext.Provider>;
}

/**
 * Home/HomeNew branch commit. The PTFW v3 intent screen (CAT-510) is a mandatory
 * redirect, and the gate reports `needsIntent: false` while its query is in flight,
 * so Home is committed only once the gate settles and says no. When it fires, the
 * view navigates away and leaving Home cancels the attempt. `redirecting` is not
 * reported here: CreatorHome owns that key and reports merge last-writer-wins.
 */
export function isHomeBranchCommitted(
  dataPending: boolean,
  intentGate: { isLoading: boolean; needsIntent: boolean }
): boolean {
  return !dataPending && !intentGate.isLoading && !intentGate.needsIntent;
}

/** Every callback closes over its commit's attempt; never looks up a newer root. */
export function useHomeReport(state: HomeState) {
  const handle = useContext(HomeContext);
  const {
    configReady,
    creatorReady,
    branchCommitted,
    socialPending,
    redirecting,
    blockingError,
    partialError,
  } = state;
  useEffect(() => {
    safely(
      () =>
        handle?.report(
          Object.fromEntries(
            Object.entries({
              configReady,
              creatorReady,
              branchCommitted,
              socialPending,
              redirecting,
              blockingError,
              partialError,
            }).filter(([, value]) => value !== undefined)
          )
        ),
      undefined
    );
  }, [
    handle,
    configReady,
    creatorReady,
    branchCommitted,
    socialPending,
    redirecting,
    blockingError,
    partialError,
  ]);
}
export function HomeBlockingError() {
  useHomeReport({ blockingError: true });
  return null;
}
